import {
  Badge,
  Box,
  Button,
  ComboboxItem,
  Flex,
  Grid,
  NumberFormatter,
  Select,
  TextInput,
} from "@mantine/core";
import { useDebouncedState, useHotkeys } from "@mantine/hooks";
import {
  IconBarcode,
  IconBuildingSkyscraper,
  IconBuildingWarehouse,
  IconFileTypeCsv,
  IconMapPin,
  IconSearch,
  IconTag,
} from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  repositoryDelivery,
  repositoryMdm,
} from "../../../_base/_const/_constVar";
import _breadcrumb from "../../../_base/component/_layout/_breadcrumb";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { changeSearchValue } from "../../../_base/helper/FunctionHelper";
import { formatDateTime } from "../../../common/FormatDate/FormatDate";
import { BASE_API_MDM } from "../../../config";
import { _localization } from "../../../config/location";
import { MessageResponse } from "../../../model/MessageResponse";
import { getMasterOrgSelect } from "../../../service/getSelectApi";

type ColumnPinningType = {
  left?: string[];
  right?: string[];
};

const ThongTinYeuCauNhapMua = () => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [data, setData] = useState<any[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [height, setHeight] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });

  const location = useLocation();
  const _searchJson = sessionStorage.getItem(String(location.pathname))
    ? JSON.parse(sessionStorage.getItem(String(location.pathname)) ?? "")
    : null;
  const checkedPinSearch = localStorage.getItem("checkedPinSearch") == "true";
  const [isCallFirst, setIsCallFirst] = useState<boolean>(false);

  // Các trường có Id -> lọc theo Id
  const [search, setSearch] = useState<any>({
    IdCn: _searchJson && checkedPinSearch ? _searchJson.IdCn : "",
    IdKho: _searchJson && checkedPinSearch ? _searchJson.IdKho : "",
    IdVtri: _searchJson && checkedPinSearch ? _searchJson.IdVtri : "",
  });

  // Các trường không có Id -> lọc theo mã
  const [keySearch, setKeySearch] = useDebouncedState<string>(
    _searchJson && checkedPinSearch ? _searchJson?.keySearch : "",
    300,
  );
  const [soPn, setSoPn] = useDebouncedState<string>(
    _searchJson && checkedPinSearch ? _searchJson?.soPn : "",
    300,
  );
  const [maVt, setMaVt] = useDebouncedState<string>(
    _searchJson && checkedPinSearch ? _searchJson?.maVt : "",
    300,
  );
  const [serial, setSerial] = useDebouncedState<string>(
    _searchJson && checkedPinSearch ? _searchJson?.serial : "",
    300,
  );
  const [maLoai, setMaLoai] = useDebouncedState<string>(
    _searchJson && checkedPinSearch ? _searchJson?.maLoai : "",
    300,
  );
  const [maChung, setMaChung] = useDebouncedState<string>(
    _searchJson && checkedPinSearch ? _searchJson?.maChung : "",
    300,
  );
  const [maNhom, setMaNhom] = useDebouncedState<string>(
    _searchJson && checkedPinSearch ? _searchJson?.maNhom : "",
    300,
  );

  const [dataBranchSelect, setDataBranchSelect] = useState<ComboboxItem[]>([]);
  const [dataInventorySelect, setDataInventorySelect] = useState<
    ComboboxItem[]
  >([]);
  const [dataPositionSelect, setDataPositionSelect] = useState<ComboboxItem[]>(
    [],
  );

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "soPn",
        header: "Số phiếu nhập",
        size: 130,
      },
      {
        accessorKey: "maVt",
        header: "Mã vật tư",
        size: 130,
      },
      {
        accessorKey: "tenVt",
        header: "Tên vật tư",
        size: 320,
      },
      {
        accessorKey: "serial",
        header: "Serial",
        size: 160,
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <Badge variant="dot" color="green">
              {renderedCellValue}
            </Badge>
          ) : (
            <></>
          ),
      },
      {
        accessorKey: "quantity",
        header: "Số lượng",
        size: 100,
      },
      {
        accessorKey: "slDn",
        header: "Số lượng đề nghị",
        size: 150,
      },
      {
        accessorKey: "giaNhap",
        header: "Giá nhập",
        size: 130,
        Cell: ({ row }) => (
          <NumberFormatter
            thousandSeparator
            value={row.original.giaNhap ?? undefined}
          />
        ),
      },
      {
        accessorKey: "ngayYc",
        header: "Ngày yêu cầu",
        size: 130,
        Cell: ({ renderedCellValue }) => (
          <>{formatDateTime(renderedCellValue as string)}</>
        ),
      },
      {
        accessorKey: "nguoiDn",
        header: "Người đề nghị",
        size: 180,
      },
      {
        accessorKey: "ngayDuyet",
        header: "Ngày duyệt",
        size: 130,
        Cell: ({ renderedCellValue }) => (
          <>{formatDateTime(renderedCellValue as string)}</>
        ),
      },
      {
        accessorKey: "nguoiDuyetDn",
        header: "Người duyệt đề nghị",
        size: 180,
      },
      {
        accessorKey: "dgDm",
        header: "Diễn giải đơn mua",
        size: 300,
      },
      {
        accessorKey: "maCn",
        header: "Mã chi nhánh",
        size: 120,
      },
      {
        accessorKey: "tenCn",
        header: "Tên chi nhánh",
        size: 220,
      },
      {
        accessorKey: "maKho",
        header: "Mã kho",
        size: 120,
      },
      {
        accessorKey: "tenKho",
        header: "Tên kho",
        size: 220,
      },
      {
        accessorKey: "maVtri",
        header: "Mã vị trí",
        size: 120,
      },
      {
        accessorKey: "tenVtri",
        header: "Tên vị trí",
        size: 220,
      },
      {
        accessorKey: "maLoai",
        header: "Mã loại",
        size: 110,
      },
      {
        accessorKey: "tenLoai",
        header: "Tên loại",
        size: 200,
      },
      {
        accessorKey: "maChung",
        header: "Mã chủng",
        size: 110,
      },
      {
        accessorKey: "tenChung",
        header: "Tên chủng",
        size: 200,
      },
      {
        accessorKey: "maNhom",
        header: "Mã nhóm",
        size: 110,
      },
      {
        accessorKey: "tenNhom",
        header: "Tên nhóm",
        size: 200,
      },
    ],
    [],
  );

  // Dựng phần query string dùng chung cho get-list và export
  const buildFilterQuery = () => {
    let url = "";
    const params = new URLSearchParams();
    Object.entries(search).forEach(([key, value]) => {
      if (typeof value === "string" && value.length > 0) {
        params.append(key, value);
      }
    });

    if (keySearch?.length > 0) {
      url += `&KeySearch=${keySearch?.trim()}`;
    }
    if (soPn?.length > 0) {
      url += `&SoPn=${soPn?.trim()}`;
    }
    if (maVt?.length > 0) {
      url += `&MaVt=${maVt?.trim()}`;
    }
    if (serial?.length > 0) {
      url += `&Serial=${serial?.trim()}`;
    }
    if (maLoai?.length > 0) {
      url += `&MaLoai=${maLoai?.trim()}`;
    }
    if (maChung?.length > 0) {
      url += `&MaChung=${maChung?.trim()}`;
    }
    if (maNhom?.length > 0) {
      url += `&MaNhom=${maNhom?.trim()}`;
    }

    return url + `&${params.toString()}`;
  };

  const exportDataExcel = async () => {
    setIsExporting(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${BASE_API_MDM}/api/v1/ThongTinYeuCauNhapMua/export-thong-tin-yeu-cau-nhap-mua?${buildFilterQuery()}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token?.replace(/"/g, "")}` },
        },
      );

      if (!response.ok) {
        NotificationExtension.Fails("Xuất Excel không thành công !");
        return;
      }

      const blob = await response.blob();
      const linkUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = linkUrl;
      link.download = `bao_cao_ma_vach_co_nguoi_de_nghi_${new Date().getTime()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(linkUrl);
      NotificationExtension.Success("Xuất Excel thành công !");
    } catch (error) {
      console.error(error);
      NotificationExtension.Fails("Xuất Excel không thành công !");
    } finally {
      setIsExporting(false);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    setIsError(false);
    try {
      const url =
        `/api/v1/ThongTinYeuCauNhapMua/get-list?Skip=${
          pagination.pageIndex * pagination.pageSize
        }&Take=${pagination.pageSize}` + buildFilterQuery();

      const res = await repositoryMdm.get<MessageResponse<any>>(url);

      if (res && res.success) {
        setData(res.data ?? []);
        setRowCount(res.totalCount ?? 0);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      setData([]);
      setRowCount(0);
      setIsError(true);
      console.error(error);
      return;
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
      setIsCallFirst(true);
      sessionStorage.setItem(
        String(location.pathname),
        JSON.stringify({
          ...search,
          keySearch: keySearch,
          soPn: soPn,
          maVt: maVt,
          serial: serial,
          maLoai: maLoai,
          maChung: maChung,
          maNhom: maNhom,
        }),
      );
    }
  };

  // Chi nhánh: value = Id (khớp oh.inv_org_id)
  const getDataBranchSelect = async () => {
    const res = await getMasterOrgSelect();

    if (res) {
      const dataSelect = res
        ?.filter((item: any) => item.text && item.value)
        ?.map((item: any) => ({
          label: `${item.att1} - ${item.text}`,
          value: String(item.value),
        }));
      setDataBranchSelect(dataSelect);
    }
  };

  // Kho: value = Id (khớp oh.sub_inv_id), lọc theo chi nhánh đang chọn
  const fetchDataInventory = async () => {
    const url = search.IdCn
      ? `/api/v1/TblDmInventory/get-list-by-branchs?Skip=0&Take=200&BranchId=${search.IdCn}`
      : `/api/v1/TblDmInventory/get-all`;

    const res = await repositoryDelivery.get<MessageResponse<any>>(url);

    if (res && res.success) {
      const dataSelect = res.data
        ?.filter((item: any) => item.id && item.code && item.name)
        ?.map((item: any) => ({
          label: `${item.code} - ${item.name}`,
          value: String(item.id),
        }));
      setDataInventorySelect(dataSelect ?? []);

      // Kho đang chọn không còn thuộc chi nhánh mới -> bỏ chọn
      if (
        search.IdKho &&
        !dataSelect?.map((i: any) => i.value)?.includes(String(search.IdKho))
      ) {
        changeSearchValue("", "IdKho", setSearch);
      }
    } else {
      setDataInventorySelect([]);
    }
  };

  // Vị trí: value = Id (khớp oh.positionId).
  // Chưa chọn kho thì vẫn nạp toàn bộ vị trí để người dùng chọn được ngay.
  const fetchDataPosition = async () => {
    const url = search.IdKho
      ? `/api/v1/TblDmItemPosition/get-select-by-subInvId?subInvId=${search.IdKho}`
      : `/api/v1/TblDmItemPosition/get-all`;

    const res = await repositoryDelivery.get<MessageResponse<any>>(url);

    if (res && res.success) {
      const seen = new Set<string>();
      const dataSelect = (res.data ?? [])
        ?.filter((item: any) => item.id && item.positionCode)
        ?.map((item: any) => ({
          label: `${item.positionCode} - ${item.positionName ?? ""}`,
          value: String(item.id),
        }))
        ?.filter((item: any) => {
          if (seen.has(item.value)) return false;
          seen.add(item.value);
          return true;
        });
      setDataPositionSelect(dataSelect ?? []);

      if (
        search.IdVtri &&
        !dataSelect?.map((i: any) => i.value)?.includes(String(search.IdVtri))
      ) {
        changeSearchValue("", "IdVtri", setSearch);
      }
    } else {
      setDataPositionSelect([]);
    }
  };

  useEffect(() => {
    if (isCallFirst) {
      fetchData();
    }
  }, [pagination?.pageIndex, pagination?.pageSize]);

  useEffect(() => {
    getDataBranchSelect();
  }, []);

  useEffect(() => {
    fetchDataInventory();
  }, [search.IdCn]);

  useEffect(() => {
    fetchDataPosition();
  }, [search.IdKho]);

  const getColumnPinningStorage = () => {
    const columnPinningStorage = localStorage.getItem(
      "columnPinningThongTinYeuCauNhapMua",
    );
    return columnPinningStorage
      ? JSON.parse(columnPinningStorage)
      : {
          left: ["mrt-row-numbers", "soPn"],
          right: [],
        };
  };

  const [columnPinning, setColumnPinning] = useState<ColumnPinningType>(
    getColumnPinningStorage(),
  );

  useEffect(() => {
    localStorage.setItem(
      "columnPinningThongTinYeuCauNhapMua",
      JSON.stringify(columnPinning),
    );
  }, [columnPinning]);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (185 + headerHeight));
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const table = useMantineReactTable({
    columns,
    data,
    enableRowNumbers: true,
    enableTopToolbar: false,
    localization: _localization,
    getRowId: (row) => `${row.soPn}-${row.maVt}-${row.serial}`,
    initialState: {
      showColumnFilters: false,
      columnPinning: { ...columnPinning },
      density: "xs",
    },
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    manualPagination: true,
    rowCount: rowCount,
    onPaginationChange: setPagination,
    onColumnPinningChange: setColumnPinning as any,
    state: {
      isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading,
      columnPinning,
    },
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["20", "50", "100"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    columnFilterDisplayMode: "popover",
  });

  useHotkeys([["Enter", () => fetchData()]]);

  return (
    <>
      <div ref={headerRef} style={{ width: "100%" }}>
        <Flex justify={"space-between"} align={"center"} w={"100%"}>
          <_breadcrumb></_breadcrumb>
          <Flex gap={"md"}>
            <Button
              leftSection={<IconFileTypeCsv size={16} />}
              onClick={() => exportDataExcel()}
              loading={isExporting}
            >
              Export Excel
            </Button>
          </Flex>
        </Flex>
        <Grid mt={10} w={"100%"}>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Nhập từ khóa tìm kiếm"
              defaultValue={keySearch}
              onChange={(e) => setKeySearch(e.currentTarget.value)}
              leftSection={<IconSearch color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Select
              placeholder="Chi nhánh"
              clearable
              searchable
              data={dataBranchSelect}
              value={search.IdCn ? String(search.IdCn) : null}
              onChange={(e) =>
                changeSearchValue(e ? e : "", "IdCn", setSearch)
              }
              leftSection={<IconBuildingSkyscraper color="#15aabf" />}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Select
              placeholder="Kho"
              clearable
              searchable
              data={dataInventorySelect}
              value={search.IdKho ? String(search.IdKho) : null}
              onChange={(e) =>
                changeSearchValue(e ? e : "", "IdKho", setSearch)
              }
              leftSection={<IconBuildingWarehouse color="#15aabf" />}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Select
              placeholder="Vị trí"
              clearable
              searchable
              data={dataPositionSelect}
              value={search.IdVtri ? String(search.IdVtri) : null}
              onChange={(e) =>
                changeSearchValue(e ? e : "", "IdVtri", setSearch)
              }
              leftSection={<IconMapPin color="#15aabf" />}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Số phiếu nhập"
              defaultValue={soPn}
              onChange={(e) => setSoPn(e.currentTarget.value)}
              leftSection={<IconTag color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Mã vật tư"
              defaultValue={maVt}
              onChange={(e) => setMaVt(e.currentTarget.value)}
              leftSection={<IconTag color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Serial"
              defaultValue={serial}
              onChange={(e) => setSerial(e.currentTarget.value)}
              leftSection={<IconBarcode color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Mã loại"
              defaultValue={maLoai}
              onChange={(e) => setMaLoai(e.currentTarget.value)}
              leftSection={<IconTag color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Mã chủng"
              defaultValue={maChung}
              onChange={(e) => setMaChung(e.currentTarget.value)}
              leftSection={<IconTag color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Mã nhóm"
              defaultValue={maNhom}
              onChange={(e) => setMaNhom(e.currentTarget.value)}
              leftSection={<IconTag color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Button
              onClick={() => fetchData()}
              leftSection={<IconSearch size={14} />}
            >
              Tìm kiếm
            </Button>
          </Grid.Col>
        </Grid>
      </div>
      <Box mt={10}>
        <MantineReactTable table={table} />
      </Box>
    </>
  );
};

export default ThongTinYeuCauNhapMua;
